import MultiTenantConfigGetDto from "../dtos/multiTenantConfigDto";
import { normalizeConfig } from "./ConfigNormalization";

const isEmptyResponse = (saved: unknown): boolean => {
  if (saved == null || saved === "") return true;
  if (typeof saved !== "object") return true;
  return Object.keys(saved as object).length === 0;
};

const sameDocument = (left: MultiTenantConfigGetDto, right: MultiTenantConfigGetDto): boolean =>
  (!!left.id && left.id === right.id) ||
  (!!left.name && left.name === right.name) ||
  (!!left.appName && left.appName === right.appName);

/**
 * Prefer the client document when the API returns an empty/sparse body after save.
 * This stops the v1 round-trip from wiping organisations and re-opening the error loop.
 */
export const resolveSavedConfig = (
  clientConfig: MultiTenantConfigGetDto,
  apiResponse: unknown
): MultiTenantConfigGetDto => {
  if (isEmptyResponse(apiResponse)) {
    return clientConfig;
  }

  const normalized = normalizeConfig(apiResponse);
  const clientOrgCount = clientConfig.organisations?.length ?? 0;
  const savedOrgCount = normalized.organisations?.length ?? 0;

  const clientHasConnections = (clientConfig.organisations ?? []).some(
    (org) => (org.namedConnectionSettings?.length ?? 0) > 0
  );
  const savedHasConnections = (normalized.organisations ?? []).some(
    (org) => (org.namedConnectionSettings?.length ?? 0) > 0
  );
  const clientHasElastic = (clientConfig.organisations ?? []).some(
    (org) => (org.namedElasticSettings?.length ?? 0) > 0
  );
  const savedHasElastic = (normalized.organisations ?? []).some(
    (org) => (org.namedElasticSettings?.length ?? 0) > 0
  );

  const responseLostStructure =
    (clientOrgCount > 0 && savedOrgCount === 0) ||
    (clientHasConnections && !savedHasConnections) ||
    (clientHasElastic && !savedHasElastic);

  if (responseLostStructure) {
    return {
      ...clientConfig,
      id: normalized.id || clientConfig.id,
      name: normalized.name || clientConfig.name,
      appName: normalized.appName || clientConfig.appName,
      created: normalized.created || clientConfig.created,
      lastUpdated: normalized.lastUpdated || clientConfig.lastUpdated,
      createdBy: normalized.createdBy || clientConfig.createdBy,
      lastUpdatedBy: normalized.lastUpdatedBy || clientConfig.lastUpdatedBy,
      configType: normalized.configType || clientConfig.configType,
      version: normalized.version || clientConfig.version,
    };
  }

  return {
    ...normalized,
    id: normalized.id || clientConfig.id,
    name: normalized.name || clientConfig.name,
    appName: normalized.appName || clientConfig.appName,
  };
};

export const replaceConfigInList = (
  configs: MultiTenantConfigGetDto[] | undefined,
  clientConfig: MultiTenantConfigGetDto,
  savedConfig: MultiTenantConfigGetDto
): MultiTenantConfigGetDto[] => {
  const list = configs ?? [];
  let replaced = false;
  const next = list.map((config) => {
    if (sameDocument(config, clientConfig)) {
      replaced = true;
      return savedConfig;
    }
    return config;
  });
  return replaced ? next : [savedConfig, ...next];
};
